/**
 * Light / dark theme. Three states: "system" follows the workstation and is
 * the default (a call floor runs in dark rooms and in daylight). The key is
 * the one of the design, so a workstation keeps its choice.
 */
import { ref } from 'vue'

export type Theme = 'system' | 'light' | 'dark'

const KEY = 'tsh-theme'

const isTheme = (v: string | null): v is Theme => v === 'system' || v === 'light' || v === 'dark'

export function readTheme(): Theme {
  // Private browsing may refuse storage: the app must still render.
  try {
    const v = localStorage.getItem(KEY)
    return isTheme(v) ? v : 'system'
  } catch {
    return 'system'
  }
}

export function applyTheme(t: Theme): void {
  const root = document.documentElement
  if (t === 'system') root.removeAttribute('data-theme')
  else root.setAttribute('data-theme', t)
  try { localStorage.setItem(KEY, t) } catch { /* lost at next load, nothing more */ }
}

const darkQuery = (): MediaQueryList | null => {
  try { return window.matchMedia('(prefers-color-scheme: dark)') } catch { return null }
}

export function effectiveTheme(t: Theme): 'light' | 'dark' {
  if (t !== 'system') return t
  return darkQuery()?.matches ? 'dark' : 'light'
}

/** Toggle order: light, dark, system. */
export const nextTheme = (t: Theme): Theme => (t === 'light' ? 'dark' : t === 'dark' ? 'system' : 'light')

/** Calls `fn` when the OS switches between light and dark; returns the unsubscribe. */
export function onSystemThemeChange(fn: () => void): () => void {
  const q = darkQuery()
  q?.addEventListener('change', fn)
  return () => q?.removeEventListener('change', fn)
}

/**
 * The theme of this browser, shared by every control that shows or changes
 * it (top bar, profile, workstation): two copies would drift apart.
 */
export const theme = ref<Theme>(readTheme())

export function setTheme(t: Theme): void {
  theme.value = t
  applyTheme(t)
}
